import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { User, Mail, Lock, Eye, EyeOff, PawPrint } from 'lucide-react';
import { motion } from 'framer-motion';
import { Header } from '../components/layout/Header';
import Footer from '../components/layout/Footer';

export function Register() {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        nombre: '',
        apellido: '',
        email: '',
        password: '',
        confirmPassword: '',
    });
    const [showPass, setShowPass] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleChange = (e) => {
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!form.nombre.trim() || !form.apellido.trim() || !form.email.trim() || !form.password) {
            setError('Completá todos los campos obligatorios');
            return;
        }

        if (form.password.length < 6) {
            setError('La contraseña debe tener al menos 6 caracteres');
            return;
        }

        if (form.password !== form.confirmPassword) {
            setError('Las contraseñas no coinciden');
            return;
        }

        setLoading(true);
        try {
            await api.register({
                nombre: form.nombre.trim(),
                apellido: form.apellido.trim(),
                email: form.email.trim(),
                password: form.password,
            });
            setSuccess(true);
            setTimeout(() => navigate('/login'), 2000);
        } catch (err) {
            const apiError = err.response?.data?.error;
            setError(apiError || 'Error al crear la cuenta');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <main className="min-h-screen bg-sp-light flex items-center justify-center px-4 py-8">
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-3xl p-8 xs:p-10 shadow-sp text-center max-w-[360px] w-full">
                    <div className="w-16 h-16 bg-sp-cta rounded-full flex items-center justify-center mx-auto mb-4">
                        <PawPrint className="text-white w-8 h-8" />
                    </div>
                    <h2 className="text-[1.3rem] font-black text-sp-text mb-2">¡Cuenta creada!</h2>
                    <p className="text-sp-body text-[0.9rem] font-semibold mb-4">Ya podés iniciar sesión con tu email y contraseña.</p>
                    <Link to="/login" className="inline-block w-full h-11 bg-sp-primary text-white rounded-5xl font-extrabold text-[0.95rem] leading-[44px] shadow-cta">
                        Ir al login
                    </Link>
                </motion.div>
            </main>
        );
    }

    return (
        <>
            <Header />
            <main className="min-h-screen bg-sp-light flex items-center justify-center px-4 xs:px-5 py-8">
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-[400px]">
                    <div className="bg-white rounded-3xl p-6 xs:p-8 shadow-sp">
                        <div className="text-center mb-6 xs:mb-8">
                            <div className="w-14 h-14 xs:w-16 xs:h-16 bg-gradient-to-br from-sp-accent to-sp-pink rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                                <User className="text-white w-7 h-7 xs:w-8 xs:h-8" />
                            </div>
                            <h1 className="text-[1.4rem] xs:text-[1.6rem] font-black text-sp-text mb-1">Crear cuenta</h1>
                            <p className="text-sp-muted text-[0.85rem] xs:text-[0.9rem] font-semibold">Protegé a tu mascota con SmartPet</p>
                        </div>

                        {error && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="bg-red-50 border border-red-200 text-red-600 text-[0.85rem] font-semibold px-4 py-3 rounded-xl mb-4">
                                ⚠️ {error}
                            </motion.div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-3.5 xs:space-y-4" noValidate>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[0.75rem] xs:text-[0.8rem] font-extrabold text-sp-text mb-1">Nombre</label>
                                    <input name="nombre" value={form.nombre} onChange={handleChange} placeholder="Juan" className="w-full h-10 xs:h-11 px-3.5 rounded-xl border-2 border-sp-light bg-sp-light/50 text-sp-text text-[0.85rem] xs:text-[0.9rem] font-semibold placeholder:text-sp-muted/50 focus:outline-none focus:border-sp-primary focus:bg-white transition-all" />
                                </div>
                                <div>
                                    <label className="block text-[0.75rem] xs:text-[0.8rem] font-extrabold text-sp-text mb-1">Apellido</label>
                                    <input name="apellido" value={form.apellido} onChange={handleChange} placeholder="Pérez" className="w-full h-10 xs:h-11 px-3.5 rounded-xl border-2 border-sp-light bg-sp-light/50 text-sp-text text-[0.85rem] xs:text-[0.9rem] font-semibold placeholder:text-sp-muted/50 focus:outline-none focus:border-sp-primary focus:bg-white transition-all" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[0.75rem] xs:text-[0.8rem] font-extrabold text-sp-text mb-1">Email</label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-sp-primary/50 w-4 h-4" />
                                    <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="tumail@ejemplo.com" autoComplete="username" className="w-full h-10 xs:h-11 pl-9 pr-3.5 rounded-xl border-2 border-sp-light bg-sp-light/50 text-sp-text text-[0.85rem] xs:text-[0.9rem] font-semibold placeholder:text-sp-muted/50 focus:outline-none focus:border-sp-primary focus:bg-white transition-all" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[0.75rem] xs:text-[0.8rem] font-extrabold text-sp-text mb-1">Contraseña</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-sp-primary/50 w-4 h-4" />
                                    <input type={showPass ? 'text' : 'password'} name="password" value={form.password} onChange={handleChange} placeholder="Mínimo 6 caracteres" autoComplete="new-password" className="w-full h-10 xs:h-11 pl-9 pr-10 rounded-xl border-2 border-sp-light bg-sp-light/50 text-sp-text text-[0.85rem] xs:text-[0.9rem] font-semibold placeholder:text-sp-muted/50 focus:outline-none focus:border-sp-primary focus:bg-white transition-all" />
                                    <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-sp-primary/60">
                                        {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="block text-[0.75rem] xs:text-[0.8rem] font-extrabold text-sp-text mb-1">Repetir contraseña</label>
                                <input type={showPass ? 'text' : 'password'} name="confirmPassword" value={form.confirmPassword} onChange={handleChange} placeholder="••••••••" autoComplete="new-password" className="w-full h-10 xs:h-11 px-3.5 rounded-xl border-2 border-sp-light bg-sp-light/50 text-sp-text text-[0.85rem] xs:text-[0.9rem] font-semibold placeholder:text-sp-muted/50 focus:outline-none focus:border-sp-primary focus:bg-white transition-all" />
                            </div>

                            <button type="submit" disabled={loading} className="w-full h-11 xs:h-12 bg-sp-primary text-white rounded-5xl font-extrabold text-[0.95rem] xs:text-base shadow-cta hover:shadow-cta-hover hover:-translate-y-0.5 active:scale-[0.98] transition-all disabled:opacity-60">
                                {loading ? 'Creando cuenta...' : 'Crear cuenta'}
                            </button>
                        </form>

                        <p className="mt-5 text-center text-[0.8rem] xs:text-[0.85rem] text-sp-body font-semibold">
                            ¿Ya tenés cuenta? <Link to="/login" className="text-sp-primary font-extrabold hover:underline">Iniciá sesión</Link>
                        </p>
                    </div>
                </motion.div>
            </main>
            <Footer />
        </>
    );
}